import { Currency } from '@principle-theorem/accounting';
import {
  isDocRef,
  isObject,
  ITimePeriod,
  Region,
  TypeGuard,
  type DocumentReference,
  type INamedDocument,
  type ISODateType,
  type WithRef,
} from '@principle-theorem/shared';
import { isNumber, isString } from 'lodash';
import { IAddressMetadata } from '../address/address';
import { type IAppointment } from '../appointment/appointment';
import { type IAppointmentSuggestion } from '../appointment/appointment-suggestion';
import { IBrand } from '../brand';
import { type ITreatmentTemplate } from '../clinical-charting/treatment/treatment-template';
import { type IDepositPaymentAttempt } from '../deposit/deposit-payment-attempt';
import { type PatientDepositType } from '../deposit/deposit-request';
import { type IPractice } from '../practice/practice';
import { type IStaffer } from '../staffer/staffer';
import { type IContactNumber } from './patient';
import { type PatientRelationshipType } from './patient-relationship';

export interface IOnlineBookingData {
  practice: WithRef<IPractice>;
  templates: WithRef<ITreatmentTemplate>[];
  brand: WithRef<IBrand>;
  currency: Currency;
  region: Region;
  connectedAccountId?: string;
}

export interface IGetBookingSuggestionsRequest {
  practice: INamedDocument<IPractice>;
  template: WithRef<ITreatmentTemplate>;
  dateRange: ITimePeriod;
  practitioner?: INamedDocument<IStaffer>;
}

export interface IHasPracticeId {
  practiceId: string;
}

export function isHasPracticeId(data: unknown): data is IHasPracticeId {
  return isObject(data) && 'practiceId' in data;
}

export function isGetBookingSuggestionRequest(
  data: unknown
): data is IGetBookingSuggestionsRequest {
  return (
    isObject(data) &&
    'practice' in data &&
    'template' in data &&
    'dateRange' in data
  );
}

export interface ISearchRescheduleAvailabilityRequest {
  practice: INamedDocument<IPractice>;
  practitioner: INamedDocument<IStaffer>;
  appointment: WithRef<IAppointment>;
  dateRange: ITimePeriod;
}

export function isSearchRescheduleAvailabilityRequest(
  data: unknown
): data is ISearchRescheduleAvailabilityRequest {
  return (
    isObject(data) &&
    'practice' in data &&
    'practitioner' in data &&
    'appointment' in data &&
    'dateRange' in data
  );
}

export interface IPatientFormData {
  relationshipType: PatientRelationshipType;
  name: string;
  dateOfBirth: ISODateType;
}

export interface IPrimaryContactFormData
  extends Omit<IPatientFormData, 'relationshipType'> {
  mobileNumber: string;
  email: string;
  address?: IAddressMetadata;
}

export interface ISuggestedPatientData {
  region: Region;
  name?: string;
  dateOfBirth?: ISODateType;
  contactNumbers?: IContactNumber[];
  mobileNumber?: string;
}

export interface IPatientData {
  primaryContact: IPrimaryContactFormData;
  patient?: IPatientFormData;
}

export interface IBookingRequest {
  patientData: IPatientData;
  suggestion: IAppointmentSuggestion;
  /**
   * Untrusted client input. The server MUST assert this ref lives under the
   * resolved brand's treatment template collection before loading it — see
   * `assertTemplateInBrand`. Skipping that lets a client point at another
   * brand's template to inherit a favourable deposit override.
   */
  templateRef: DocumentReference<ITreatmentTemplate>;
  /** Display/analytics only. Never an input to deposit derivation. */
  treatmentName: string;
  conversionUid?: string;
  /**
   * Untrusted client input, same as `templateRef` — the server MUST assert it
   * resolves under the booking practice's payment attempt collection before
   * loading it.
   */
  depositPaymentAttemptRef?: DocumentReference<IDepositPaymentAttempt>;
  depositAmount?: number;
  depositType?: PatientDepositType;
}

const isBookingRequestShape = TypeGuard.interface<IBookingRequest>({
  patientData: isObject,
  suggestion: isObject,
  templateRef: isDocRef,
  treatmentName: (value): value is string =>
    isString(value) && value.trim().length > 0,
  conversionUid: TypeGuard.nilOr(isString),
  depositPaymentAttemptRef: TypeGuard.nilOr(isDocRef),
  depositAmount: TypeGuard.nilOr(isNumber),
  depositType: TypeGuard.nilOr(isString),
});

export function isBookingRequest(data: unknown): data is IBookingRequest {
  if (!isObject(data)) {
    return false;
  }

  const hasLegacyTemplateBody =
    'template' in data &&
    typeof data.template === 'object' &&
    data.template !== undefined;
  const hasLegacyTemplateId = 'templateId' in data || 'stepId' in data;

  return (
    !hasLegacyTemplateBody &&
    !hasLegacyTemplateId &&
    isBookingRequestShape(data)
  );
}

export interface IPageViewRequest {
  conversionUid: string;
}

export function isPageViewRequest(data: unknown): data is IPageViewRequest {
  return isObject(data) && 'conversionUid' in data;
}
